import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { Cart } from "@NA/Overrides/Services/CartService";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonWrapper } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { css } from "styled-components";
import ADILiveChatComponent from "./ADILiveChatComponent";
import { StyledButton } from "@insite/mobius/Clickable";

interface ADICannotShipModalProps {
    isOpen: boolean;
    handleClose: Function;
    headline: string;
    cart?: Readonly<Cart>;
    removeItemFunc: Function;
    returnToQuoteDetails: Function;
    pickUpBranchFunc: Function;
}

export interface ADICannotShipModalStyles {
    availabilityByWarehouseModal?: ModalPresentationProps;
    modalDescription?: InjectableCss;
    dataTableWrapper?: InjectableCss;
    dataTableBody?: InjectableCss;
    dgDataHead?: InjectableCss;
    adiPartHeader?: InjectableCss;
    adiModelHeader?: InjectableCss;
    adiDescHeader?: InjectableCss;
    dgDataContainer?: InjectableCss;
    adiPartContainer?: InjectableCss;
    adiModelContainer?: InjectableCss;
    adiDescContainer?: InjectableCss;
    buttonContainer?: InjectableCss;
    removeItemButton?: InjectableCss;
    returnToQuotes?: InjectableCss;
    pickUpInBranchButton?: InjectableCss;
}

export const adiCannotShipModalStyles: ADICannotShipModalStyles = {
    availabilityByWarehouseModal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalBody: css`
                max-width: 1016px !important;
                border-radius: 4px;
                padding: 28px !important;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: calc(100% - 30px);
                            height: auto;
                            max-height: calc(100% - 30px);
                            padding: 15px !important;
                        `,
                        css`
                            width: calc(100% - 30px);
                            height: auto;
                            max-height: (100% - 30px);
                            padding: 15px !important;
                        `,
                        css`
                            width: calc(100% - 65px);
                            height: auto;
                            max-height: (100% - 65px);
                            padding: 20px !important;
                            position: absolute;
                            top: 16%;
                        `,
                        null,
                        null,
                    ])}
            `,
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                border-bottom: 0;
                padding: 0 !important;
                margin-bottom: 18px;
                > h4 {
                    font-size: 28px;
                    font-weight: 700;
                    font-stretch: normal;
                    font-style: normal;
                    line-height: 32px;
                    letter-spacing: normal;
                    color: ${ADIColors.darkBlue1};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: 24px;
                                line-height: 28px;
                            `,
                            css`
                                font-size: 24px;
                                line-height: 28px;
                            `,
                            css`
                                font-size: 24px;
                                line-height: 28px;
                            `,
                            null,
                            null,
                        ])}
                }
                button {
                    border-radius: 0;
                    display: none;
                    svg {
                        height: 20px;
                    }
                }
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    modalDescription: {
        css: css`
            line-height: inherit;
            font-size: 18px;
            line-height: 24px;
            color: ${ADIColors.black};
            margin-bottom: 9px;
            font-weight: 400;
            ${StyledButton} {
                color: ${ADIColors.blue5};
                text-decoration: underline;
                &:hover {
                    color: ${ADIColors.blue1};
                }
            }
            ${TypographyStyle} {
                font-size: 16px;
                line-height: 24px;
                color: ${ADIColors.blue5};
                text-decoration: underline;
                font-weight: 400;
                &:hover {
                    color: ${ADIColors.blue1};
                }
            }
        `,
    },
    dataTableWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                        overflow-x: auto;
                    `,
                    css`
                        display: flex;
                        flex-direction: column;
                        overflow-x: auto;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dataTableBody: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-width: fit-content;
                        background: ${ADIColors.brightGray1};
                    `,
                    css`
                        min-width:fit-content;
                        background: ${ADIColors.brightGray1};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    dgDataHead: {
        css: css`
            display: flex;
        `,
    },
    dgDataContainer: {
        css: css`
            display: flex;
            background-color: ${ADIColors.brightGray1};
        `,
    },
    adiPartHeader: {
        css: css`
            width: 20%;
            font-size: 16px;
            line-height: 22px;
            padding: 9px 23px;
            color: ${ADIColors.black};
            font-weight: 700;
            -webkit-text-size-adjust: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    adiPartContainer: {
        css: css`
            width: 20%;
            color: ${ADIColors.black};
            font-size: 16px;
            line-height: 22px;
            padding: 9px 23px;
            display: inline-flex;
            justify-content: flex-start;
            align-items: center;
            -webkit-text-size-adjust: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    adiModelHeader: {
        css: css`
            width: 25%;
            font-size: 16px;
            line-height: 22px;
            padding: 9px 23px;
            color: ${ADIColors.black};
            font-weight: bold;
            -webkit-text-size-adjust: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    adiModelContainer: {
        css: css`
            width: 25%;
            color: ${ADIColors.black};
            font-size: 16px;
            line-height: 22px;
            padding: 9px 23px;
            display: inline-flex;
            justify-content: flex-start;
            align-items: center;
            -webkit-text-size-adjust: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    css`
                        width: auto;
                        min-width: 130px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    adiDescHeader: {
        css: css`
            width: 55%;
            font-size: 16px;
            line-height: 22px;
            padding: 9px 23px;
            color: ${ADIColors.black};
            font-weight: bold;
            -webkit-text-size-adjust: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: auto;
                        min-width: 180px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    css`
                        width: auto;
                        min-width: 180px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    adiDescContainer: {
        css: css`
            width: 55%;
            color: ${ADIColors.black};
            font-size: 16px;
            line-height: 22px;
            padding: 9px 23px;
            display: inline-flex;
            justify-content: flex-start;
            align-items: center;
            -webkit-text-size-adjust: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        width: auto;
                        min-width: 180px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    css`
                        width: auto;
                        min-width: 180px;
                        padding: 9px 5px;
                        white-space: nowrap;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    buttonContainer: {
        css: css`
            margin-top: 20px;
            display: flex;
            flex-direction: row;
            align-items: flex-end;
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        align-items: center;
                        gap: 10px;
                        flex-direction: column;
                        display: flex;
                        > ${ButtonWrapper} {
                            min-width: 160px;
                            margin-left: 0px;
                        }
                    `,
                    css`
                        align-items: center;
                        gap: 10px;
                        flex-direction: column;
                        display: flex;
                        > ${ButtonWrapper} {
                            min-width: 160px;
                            margin-left: 0px;
                        }
                    `,
                    css`
                        ${ButtonWrapper} {
                            span {
                                font-size: 14px;
                            }
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    removeItemButton: {
        css: css`
            background-color: ${ADIColors.blue5};
            border-radius: 4px;
            padding: 10px 16px;
            min-height: 42px;
            height: auto;
            text-transform: none;
            font-size: 16px;
            min-width: 156px;
            font-weight: 700;
            &:hover {
                background-color: ${ADIColors.blue5};
                border-color: ${ADIColors.blue5};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        span {
                            font-size: 14px;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    returnToQuotes: {
        css: css`
            min-width: initial;
            height: 42px;
            font-size: 14px;
            font-weight: 600;
            padding: 0 16px;
            text-align: center;
            border-radius: 4px;
            margin: 0;
            &:hover {
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        span {
                            font-size: 14px;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
    pickUpInBranchButton: {
        css: css`
            min-width: initial;
            height: 42px;
            font-size: 14px;
            font-weight: 600;
            padding: 0 16px;
            text-align: center;
            border-radius: 4px;
            margin: 0;
            margin-left: 20px;
            &:hover {
                background: ${ADIColors.darkBlue6} !important;
                color: ${ADIColors.blue1} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        span {
                            font-size: 14px;
                        }
                    `,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = adiCannotShipModalStyles;

const ADICannotShipModal = ({
    isOpen,
    handleClose,
    headline,
    removeItemFunc,
    returnToQuoteDetails,
    cart,
    pickUpBranchFunc,
}: ADICannotShipModalProps) => {
    if (!cart) {
        return null;
    }
    const isLbProductPresntInCart = cart?.cartLines?.some(e => e.properties?.logicBroker?.toLowerCase() === "yes");
    const filteredLBProduct = cart?.cartLines?.filter(
        e => e.properties?.logicBroker?.toLowerCase() === "yes" && e.qtyOrdered !== 0,
    );

    return (
        <Modal
            headline={headline}
            handleClose={() => handleClose()}
            isCloseable={false}
            isOpen={isOpen}
            {...styles.availabilityByWarehouseModal}
        >
            <StyledWrapper>
                <StyledWrapper {...styles.modalDescription}>
                    {cart?.type.toLowerCase() !== "quote" ? (
                        "The following items are considered dangerous goods and cannot be shipped to addresses outside of the contiguous United States. Click Remove Item(s) to delete these products from your cart and continue shopping."
                    ) : (
                        <>
                            The following items are considered dangerous goods and cannot be shipped to addresses
                            outside of the contiguous United States. To remove an Item from your quote, contact a
                            customer service representative at{" "}
                            <Link href="tel:1.877.228.6739">{translate("1.877.228.6739")}</Link> or{" "}
                            <ADILiveChatComponent isModal={true} /> from 8 a.m to 7pm ET.
                        </>
                    )}
                </StyledWrapper>
                <StyledWrapper {...styles.dataTableWrapper}>
                    <StyledWrapper {...styles.dgDataHead}>
                        <StyledWrapper {...styles.adiPartHeader}>ADI Part #</StyledWrapper>
                        <StyledWrapper {...styles.adiModelHeader}>Model #</StyledWrapper>
                        <StyledWrapper {...styles.adiDescHeader}>Description</StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.dataTableBody}>
                        {cart.cartLines &&
                            filteredLBProduct &&
                            filteredLBProduct?.length > 0 &&
                            filteredLBProduct?.map((ele, index) => (
                                <StyledWrapper key={index}>
                                    {ele.properties.cannotShipAir === "Y" && ele.properties.dgItem === "True" ? (
                                        <StyledWrapper {...styles.dgDataContainer}>
                                            <StyledWrapper {...styles.adiPartContainer}>{ele.erpNumber}</StyledWrapper>
                                            <StyledWrapper {...styles.adiModelContainer}>
                                                {ele.modelNumber !== "" ? ele.modelNumber : ele.manufacturerItem}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.adiDescContainer}>
                                                {ele.shortDescription}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            ))}
                        {cart.cartLines &&
                            cart.cartLines?.map((ele, index) => (
                                <StyledWrapper key={index}>
                                    {ele.properties.cannotShipAir === "Y" &&
                                    (ele.properties?.logicBroker === undefined ||
                                        ele.properties?.logicBroker?.toLowerCase() === "no") &&
                                    ele.properties.dgItem === "True" ? (
                                        <StyledWrapper {...styles.dgDataContainer}>
                                            <StyledWrapper {...styles.adiPartContainer}>{ele.erpNumber}</StyledWrapper>
                                            <StyledWrapper {...styles.adiModelContainer}>
                                                {ele.modelNumber !== "" ? ele.modelNumber : ele.manufacturerItem}
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.adiDescContainer}>
                                                {ele.shortDescription}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ) : (
                                        ""
                                    )}
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.buttonContainer}>
                    {cart?.type.toLowerCase() !== "quote" ? (
                        <Button {...styles.removeItemButton} onClick={() => removeItemFunc()}>
                            Remove Item(s)
                        </Button>
                    ) : (
                        isLbProductPresntInCart && (
                            <Button {...styles.returnToQuotes} onClick={() => returnToQuoteDetails()}>
                                Return to Quotes
                            </Button>
                        )
                    )}
                    {!isLbProductPresntInCart && (
                        <Button {...styles.pickUpInBranchButton} onClick={() => pickUpBranchFunc()}>
                            Pick Up In Branch
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </Modal>
    );
};

export default ADICannotShipModal;
